import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { classNames } from "../../helpers/classNames";

const TextAreaAutoSize = forwardRef(({
  label,
  labelClasses,
  inputValue = "",
  inputClasses,
  inputPlaceholder = "",
  inputName,
  isDisabled = false,
  error = false,
  onChange = () => { },
  errorType,
  errorText,
  isValidate,
  onBlur = () => { },
  currentHeight = '80px'
}, ref) => {
  const buildInputClasses = classNames(
    "w-full min-h-[80px] max-h-[160px] rounded-md placeholder:text-md-dark bg-md-pink-light-2 border border-md-pink-light-1 px-3 text-md-dark text-base !ring-0 !outline-0 focus:border-md-pink/40 transition-all duration-200 resize-none scrollbar",
    error ? "border-red-300" : "",
    isDisabled
      ? "text-red-400 cursor-not-allowed bg-red-100 !border-red-200"
      : "",
    inputClasses
  );
  const [val, setVal] = useState(inputValue);
  const textareaRef = useRef(null);

  // Expose the focus method to parent components
  useImperativeHandle(ref, () => ({
    focus: () => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }
  }));

  const handleKeyDown = () => {
    textareaRef.current.style.height = currentHeight;
    textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
  };

  useEffect(() => {
    setVal(inputValue);
  }, [inputValue]);

  useEffect(() => {
    if (textareaRef) {
      handleKeyDown();
    }
    if (!val) {
      textareaRef.current.style.height = currentHeight;
    }
  }, [val, textareaRef, currentHeight]);

  return (
    <>
      <div className="relative space-y-1 w-full">
        {label && (
          <div
            className={classNames(
              "text-xs font-medium text-shades-07 font-Poppins mb-1",
              labelClasses
            )}
          >
            {label}
            {isValidate && (
              <span className="text-[8px] text-red-500 ml-0.5 absolute top-[-2px]">
                <i className="fa-solid fa-asterisk"></i>
              </span>
            )}
          </div>
        )}
        <div className="relative flex">
          <textarea
            disabled={isDisabled}
            ref={textareaRef}
            value={val}
            rows={1}
            name={inputName}
            placeholder={inputPlaceholder}
            className={buildInputClasses}
            onChange={(e) => {
              setVal(e.target.value);
              onChange(e);
            }}
            onBlur={onBlur}
            onPaste={handleKeyDown}
            onFocus={handleKeyDown}
          ></textarea>
        </div>
        {errorType && errorType === "error" && (
          <div className="text-xs text-red-600 mt-1">
            <i className="fa-regular fa-fw fa-square-exclamation text-sm mr-1"></i>
            {errorText}
          </div>
        )}
        {errorType && errorType === "warning" && (
          <div className="text-xs text-amber-600 mt-1">
            <i className="fa-regular fa-fw fa-triangle-exclamation text-sm mr-1"></i>
            {errorText}
          </div>
        )}
        {errorType && errorType === "success" && (
          <div className="text-xs text-green-600 mt-1">
            <i className="fa-regular fa-fw fa-circle-check text-sm mr-1"></i>
            {errorText}
          </div>
        )}
        {errorType && errorType === "info" && (
          <div className="text-xs text-sky-600 mt-1">
            <i className="fa-regular fa-fw fa-circle-info text-sm mr-1"></i>
            {errorText}
          </div>
        )}
      </div>
    </>
  );
});

TextAreaAutoSize.displayName = 'TextAreaAutoSize';

export default TextAreaAutoSize;